import { ErrorCode } from './types'
import type { ApiError } from './api'

/**
 * User-friendly error messages for known error codes
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Rate limit errors
  [ErrorCode.RATE_LIMIT]: 'You\'re making requests too quickly. Please wait a moment and try again.',
  [ErrorCode.TOO_MANY_REQUESTS]: 'You\'re making requests too quickly. Please wait a moment and try again.',
  [ErrorCode.TWITTER_RATE_LIMIT]: 'X rate limit reached. Please wait a few minutes before trying again.',
  [ErrorCode.LLM_RATE_LIMIT]: 'AI service is busy. Please wait a moment and try again.',

  // Auth errors
  [ErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password.',
  [ErrorCode.EMAIL_ALREADY_EXISTS]: 'An account with this email already exists.',
  [ErrorCode.UNAUTHORIZED]: 'Please log in to continue.',
  [ErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',

  // Twitter errors
  [ErrorCode.TWITTER_NOT_CONNECTED]: 'Please connect your X account first.',
  [ErrorCode.TWITTER_API_ERROR]: 'Unable to connect to X. Please try again later.',

  // Platform errors
  [ErrorCode.SOCIAL_ACCOUNT_NOT_CONNECTED]: 'Please connect your account first.',
  [ErrorCode.PLATFORM_API_ERROR]: 'Unable to reach the platform. Please try again later.',
  [ErrorCode.PLATFORM_RATE_LIMIT]: 'Platform rate limit reached. Please wait a few minutes.',
  [ErrorCode.PLATFORM_AUTH_EXPIRED]: 'Platform authorization has expired. Please reconnect your account.',

  // Instagram errors
  [ErrorCode.INSTAGRAM_MEDIA_REQUIRED]: 'Instagram posts require at least one image.',
  [ErrorCode.INSTAGRAM_CONTAINER_FAILED]: 'Failed to prepare Instagram post. Please try again.',

  // Media errors
  [ErrorCode.MEDIA_UPLOAD_FAILED]: 'Failed to upload media. Please try again.',
  [ErrorCode.MEDIA_TOO_LARGE]: 'File is too large. Maximum size is 8MB.',
  [ErrorCode.UNSUPPORTED_MEDIA_TYPE]: 'Unsupported file type. Use JPEG, PNG, or WebP.',

  // Voice errors
  [ErrorCode.NO_VOICE_PROFILE]: 'No voice profile found. Analyze your posts to create one.',
  [ErrorCode.VOICE_ANALYSIS_IN_PROGRESS]: 'Voice analysis is already in progress.',
  [ErrorCode.INSUFFICIENT_TWEETS]: 'Not enough posts to analyze. You need at least 50 original posts.',

  // Account errors
  [ErrorCode.ACCOUNT_NOT_FOUND]: 'This account was not found. It may already have been removed.',
  [ErrorCode.ACCOUNT_MUST_BE_DISCONNECTED]: 'Disconnect this account first, then you can remove it from Beacon.',

  // Content errors
  [ErrorCode.CONTENT_NOT_FOUND]: 'Content not found.',
  [ErrorCode.CONTENT_ALREADY_POSTED]: 'This content has already been posted.',

  // Schedule errors
  [ErrorCode.SCHEDULE_IN_PAST]: 'Scheduled time must be in the future.',
  [ErrorCode.SCHEDULE_TOO_FAR]: 'Scheduled time cannot be more than 30 days from now.',

  // LLM errors
  [ErrorCode.LLM_API_ERROR]: 'AI service is temporarily unavailable. Please try again.',

  // General errors
  [ErrorCode.INTERNAL_ERROR]: 'Something went wrong. Please try again later.',
  [ErrorCode.NETWORK_ERROR]: 'Unable to connect to the server. Please check your connection.',
}

/**
 * Rate limit related error codes
 */
const RATE_LIMIT_CODES = new Set([
  ErrorCode.RATE_LIMIT,
  ErrorCode.TOO_MANY_REQUESTS,
  ErrorCode.TWITTER_RATE_LIMIT,
  ErrorCode.PLATFORM_RATE_LIMIT,
  ErrorCode.LLM_RATE_LIMIT,
  'RATE_LIMIT',
  'TOO_MANY_REQUESTS',
])

/**
 * Check if an error is a rate limit error
 */
export function isRateLimitError(error: ApiError): boolean {
  return RATE_LIMIT_CODES.has(error.code)
}

/**
 * Get a user-friendly error message for an API error
 *
 * @param error - The ApiError to get a message for
 * @param fallback - Optional fallback message if no specific message is found
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: ApiError, fallback?: string): string {
  // Check for known error codes first
  const knownMessage = ERROR_MESSAGES[error.code]
  if (knownMessage) {
    return knownMessage
  }

  // Check if it's a rate limit error by message content
  const lowerMessage = error.message.toLowerCase()
  if (
    lowerMessage.includes('rate limit') ||
    lowerMessage.includes('too many requests') ||
    lowerMessage.includes('too many')
  ) {
    return 'You\'re making requests too quickly. Please wait a moment and try again.'
  }

  // Use the error message if it's user-friendly (not a generic server error)
  if (error.message && !error.message.includes('Internal') && error.message.length < 200) {
    return error.message
  }

  // Use fallback or generic message
  return fallback || 'Something went wrong. Please try again.'
}

/**
 * Get retry delay hint from error details if available
 */
export function getRetryAfter(error: ApiError): number | null {
  const retryAfter = error.details?.retry_after as number | undefined
  if (typeof retryAfter === 'number' && retryAfter > 0) {
    return retryAfter
  }
  return null
}
